import { unitDef, type PlayerId, type UnitKind } from "@dailywars/core/engine";
import { MoveLeft, MoveRight, Skull } from "lucide-react";

import { Button } from "#/components/ui/button";
import { getBuildingIconSrc } from "#/game/buildingSprites";
import { getSpriteUnitIconImage } from "#/game/unitSprites";
import { cn } from "#/lib/utils";

import {
  gameDockedPanelFooterClassName,
  gameDockedPanelSurfaceClassName,
  gameDockedPanelTitleClassName,
} from "./GameDockedPanelShell";

type AttackTargetKind = UnitKind | "cannon";

interface DamageForecastProps {
  attackerKind: UnitKind;
  attackerOwner: PlayerId;
  targetKind: AttackTargetKind;
  targetOwner: PlayerId;
  forecast: number;
  counterForecast: number | null;
  willKillTarget: boolean;
  willKillAttacker: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  className?: string;
}

function targetDisplayName(kind: AttackTargetKind): string {
  return kind === "cannon" ? "Cannon" : unitDef(kind).name;
}

function UnitPortrait({ kind, owner }: { kind: UnitKind; owner: PlayerId }) {
  const img = getSpriteUnitIconImage(kind, owner);
  const name = unitDef(kind).name;

  return (
    <img
      src={img.src}
      alt={name}
      className={cn("h-14 w-14 rounded-sm", owner === "p2" && "-scale-x-100")}
    />
  );
}

function TargetPortrait({ kind, owner }: { kind: AttackTargetKind; owner: PlayerId }) {
  if (kind === "cannon") {
    return (
      <img
        src={getBuildingIconSrc("cannon", owner)}
        alt="Cannon"
        className="h-14 w-14 rounded-sm"
      />
    );
  }
  return <UnitPortrait kind={kind} owner={owner} />;
}

function DamagePercent({ value, willKill }: { value: number; willKill: boolean }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-xs tabular-nums font-semibold text-foreground">
      {value}%
      {willKill && (
        <span className="inline-flex items-center">
          (<Skull aria-hidden className="size-3.5 text-red-300" />)
        </span>
      )}
    </span>
  );
}

function DamageArrows({
  forecast,
  counterForecast,
  willKillTarget,
  willKillAttacker,
}: {
  forecast: number;
  counterForecast: number | null;
  willKillTarget: boolean;
  willKillAttacker: boolean;
}) {
  const canCounter = counterForecast !== null;
  return (
    <div className="flex min-w-10 flex-col items-center justify-center gap-0.5">
      <DamagePercent value={forecast} willKill={willKillTarget} />
      <MoveRight aria-hidden className="size-5 text-muted-foreground" strokeWidth={2.5} />
      {canCounter && (
        <>
          <MoveLeft aria-hidden className="size-5 text-muted-foreground" strokeWidth={2.5} />
          <DamagePercent value={counterForecast} willKill={willKillAttacker} />
        </>
      )}
    </div>
  );
}

function DamageForecastMatchup({
  attackerKind,
  attackerOwner,
  targetKind,
  targetOwner,
  forecast,
  counterForecast,
  willKillTarget,
  willKillAttacker,
}: {
  attackerKind: UnitKind;
  attackerOwner: PlayerId;
  targetKind: AttackTargetKind;
  targetOwner: PlayerId;
  forecast: number;
  counterForecast: number | null;
  willKillTarget: boolean;
  willKillAttacker: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-2">
      <UnitPortrait kind={attackerKind} owner={attackerOwner} />
      <DamageArrows
        forecast={forecast}
        counterForecast={counterForecast}
        willKillTarget={willKillTarget}
        willKillAttacker={willKillAttacker}
      />
      <TargetPortrait kind={targetKind} owner={targetOwner} />
    </div>
  );
}

function DamageForecastActions({
  onConfirm,
  onCancel,
  disabled,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
}) {
  return (
    <>
      <Button
        onClick={onCancel}
        disabled={disabled}
        variant="outline"
        size="default"
        className="flex-1"
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={disabled}
        variant="default"
        size="default"
        className="flex-1"
      >
        Attack
      </Button>
    </>
  );
}

export function DamageForecast({
  attackerKind,
  attackerOwner,
  targetKind,
  targetOwner,
  forecast,
  counterForecast,
  willKillTarget,
  willKillAttacker,
  onConfirm,
  onCancel,
  disabled,
  className,
}: DamageForecastProps) {
  const targetName = targetDisplayName(targetKind);
  const canCounter = counterForecast !== null;
  const ariaParts = [
    `Attack ${targetName} for ${forecast}% damage`,
    willKillTarget ? "target will be destroyed" : null,
    canCounter ? `counterattack for ${counterForecast}% damage` : "no counterattack",
    willKillAttacker ? "attacker will be destroyed" : null,
  ].filter(Boolean);

  return (
    <div
      role="dialog"
      aria-label={ariaParts.join(", ")}
      className={cn(gameDockedPanelSurfaceClassName, "w-fit", className)}
    >
      <p className={gameDockedPanelTitleClassName}>Attack {targetName}?</p>

      <DamageForecastMatchup
        attackerKind={attackerKind}
        attackerOwner={attackerOwner}
        targetKind={targetKind}
        targetOwner={targetOwner}
        forecast={forecast}
        counterForecast={counterForecast}
        willKillTarget={willKillTarget}
        willKillAttacker={willKillAttacker}
      />
      <div className={cn(gameDockedPanelFooterClassName, "flex-row")}>
        <DamageForecastActions onConfirm={onConfirm} onCancel={onCancel} disabled={disabled} />
      </div>
    </div>
  );
}
